import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useNavModule, type NavModule } from '../../../core/contexts/NavModuleContext';
import { SIDEBAR_WIDTH } from './AppSidebar';
// Imported from `src/assets/` so Vite bundles + hashes it; the build emits
// the file under `dist/assets/` with a long-cache filename.
import logoUrl from '../../../assets/logo.jpeg';

/**
 * Tab definitions for the top-level "module" switcher. `disabled: true`
 * still renders the tab so users know the feature is planned, but the
 * underlying button doesn't fire (`Tabs` skips selection for disabled
 * tabs).
 */
const MODULE_TABS: ReadonlyArray<{ value: NavModule; label: string; disabled?: boolean }> = [
  { value: 'customer', label: 'Customer Management' },
  { value: 'customViews', label: 'Custom Views' },
  { value: 'global', label: 'Global Settings' },
  { value: 'reports', label: 'Reports Monitoring' },
];

export function AppHeader() {
  const { user, logout } = useAuth();
  const { module, setModule } = useNavModule();
  const navigate = useNavigate();

  // Switching modules drops the user on that module's landing page so they
  // don't end up on a sidebar entry that doesn't belong to the new module.
  const MODULE_LANDING: Record<NavModule, string> = {
    customer: '/',
    global: '/tenants',
    reports: '/report-instances',
    customViews: '/custom-views',
  };

  const handleModuleChange = (next: NavModule) => {
    setModule(next);
    navigate(MODULE_LANDING[next]);
  };

  const initial = user?.name?.[0] ?? user?.email?.[0] ?? '?';

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (t) => t.zIndex.drawer + 1,
        backgroundColor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar sx={{ pl: 3 }}>
        {/* Logo block lines up with the sidebar. The module tabs sit
            directly after the logo so the top-level navigation is
            always one glance away. User controls stay on the far right. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ width: SIDEBAR_WIDTH - 24, mr: 3 }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt="BrandLight"
            sx={{
              height: 32,
              width: 32,
              objectFit: 'contain',
              borderRadius: 1,
              // Soft border keeps a white-background logo readable on the
              // header without forcing a hard edge.
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" color="primary" lineHeight={1.1} noWrap>
              BrandLight
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Backoffice
            </Typography>
          </Box>
        </Stack>

        {/* Top-level module switcher. `value` is bound to the context so
            the sidebar (and anything else) can react instantly. */}
        <Tabs
          value={module}
          onChange={(_, next: NavModule) => handleModuleChange(next)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            // Stretch to the Toolbar's full height so the indicator
            // underline meets the AppBar's bottom border cleanly.
            alignSelf: 'stretch',
            '& .MuiTab-root': { minHeight: 64 },
          }}
        >
          {MODULE_TABS.map((t) => (
            <Tab key={t.value} value={t.value} label={t.label} disabled={t.disabled} />
          ))}
        </Tabs>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" spacing={2} alignItems="center">
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" fontWeight={600}>
              {user?.name ?? user?.email}
            </Typography>
          </Box>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {initial.toUpperCase()}
          </Avatar>
          <Tooltip title="Sign out">
            <IconButton onClick={logout} size="small">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Toolbar>
    </AppBar>
  );
}
